'use client';

import { cn } from '@/lib/utils';
import { ForwardedRef, forwardRef, Fragment, useMemo, useState } from 'react';
import Markdown, { MarkdownToJSX } from 'markdown-to-jsx';
import { useSelectionMenu } from '@/hooks/useSelectionMenu';
import { SelectionMenuPortal } from '../common/SelectionMenuPortal';
import Card from '../common/Card';
import { useCreateChat } from '@/hooks/useCreateChat';
import { Loader2 } from 'lucide-react';
import { Divider } from '@mui/material';
import { useAsyncEffect } from 'ahooks';
import { researchFetch } from '@/lib/utils/fetch';
import ShareQuote from '../ShareModal/ShareQuote';
import { useTranslation } from '@/lib/i18n';
import Table from './overrides/Table';
import { normalizeInlineMathForMarkdown } from './inlineMath';

interface MarkdownContentProps {
  options?: MarkdownToJSX.Options;
  children: string;
  query?: string;
  messageId?: string;
  selectable?: boolean; // 是否允许选中，默认为 true
}
const MarkdownContent = forwardRef(function MarkdownContent(
  props: MarkdownContentProps,
  ref?: ForwardedRef<HTMLDivElement>,
) {
  const { options: markdownOptions = {}, children, messageId, query, selectable } = props;
  const [open, setOpen] = useState(false);
  const [paragraphContent, setParagraphContent] = useState<string[]>([]);
  const { visible, rect, text, copy, addToChat, splitContent } = useSelectionMenu({
    container: ref as React.RefObject<HTMLElement> | undefined,
    selectable,
  });
  const { createChat, isCreating } = useCreateChat('alfa');
  const { t } = useTranslation();

  const handleShare = async () => {
    const content = splitContent();
    if (content) {
      const { beforeText, selectedText, afterText } = content;
      setParagraphContent([beforeText, selectedText, afterText]);
      setOpen(true);
    }
  };

  const mdOptions = useMemo(
    () => ({
      ...markdownOptions,
      overrides: {
        ...markdownOptions.overrides,
        a: {
          component: ({ href, children, ...props }: any) => {
            let url = href;
            try {
              url = new URL(href);
              const isExternal = url.origin !== window.location.origin;
              return (
                <a
                  href={href}
                  target={isExternal ? '_blank' : undefined}
                  rel={isExternal ? 'noopener noreferrer' : undefined}
                  {...props}
                >
                  {children}
                </a>
              );
            } catch {
              return (
                <a href={url} target="_blank" rel="noopener noreferrer" {...props}>
                  {children}
                </a>
              );
            }
          },
        },
        table: Table,
      },
    }),
    [markdownOptions],
  );
  const normalizedChildren = useMemo(
    () => normalizeInlineMathForMarkdown(children),
    [children],
  );

  useAsyncEffect(async () => {
    try {
      if (messageId) {
        researchFetch(`api/search/${messageId}/read`, {
          method: 'POST',
          body: JSON.stringify({ read: true }),
        });
      }
    } catch (error) {
      console.error('Failed to sync markdown content:', error);
    }
  }, [messageId]);

  return (
    <>
      <div
        ref={ref}
        className={cn(
          'markdown-wrapper',
          // 表头样式
          '[&_th]:p-2.5',
          '[&_th]:tracking-wider [&_th]:text-text-primary',
          '[&_th]:border-b [&_th]:border-gray-200 dark:[&_th]:border-[#1f1f1f]',
          '[&_th]:whitespace-nowrap',
          // 表体样式
          '[&_tbody_tr]:border-b [&_tbody_tr]:border-gray-200 dark:[&_tbody_tr]:border-[#1f1f1f]',
          '[&_tbody_tr:hover]:bg-gray-50 dark:[&_tbody_tr:hover]:bg-[#171717]',
          '[&_tbody_tr:last-child]:border-b-0',
          // 单元格样式
          '[&_td]:p-2.5 [&_td]:font-light',
          '[&_td]:align-top [&_td]:break-words',
          '[&_td]:text-[#050505] dark:[&_td]:text-[#b7b9bc]',
        )}
      >
        <Markdown
          className={cn(
            'max-w-none font-light text-text-primary wrap-break-word',
            '[&_h1]:mb-6 [&_h1]:mt-8 [&_h1]:text-[30px] [&_h1]:font-normal [&_h1]:leading-tight [&_h1]:text-text-primary',
            '[&_h2]:mb-6 [&_h2]:mt-6 [&_h2]:text-[24px] [&_h2]:font-normal [&_h2]:leading-tight [&_h2]:text-text-primary',
            '[&_h3]:mb-4 [&_h3]:mt-5 [&_h3]:text-[20px] [&_h3]:font-normal [&_h3]:leading-snug [&_h3]:text-text-primary',
            '[&_h4]:mb-3 [&_h4]:mt-4 [&_h4]:text-base [&_h4]:font-normal [&_h4]:text-text-primary',
            '[&_h5]:mb-3 [&_h5]:mt-4 [&_h5]:text-base [&_h5]:font-normal [&_h5]:text-text-primary',
            '[&_h6]:mb-3 [&_h6]:mt-4 [&_h6]:text-base [&_h6]:font-normal [&_h6]:text-text-primary',
            '[&_p]:mb-2 [&_p]:leading-7',
            '[&_strong]:font-semibold [&_strong]:text-text-primary',
            '[&_a]:text-brand [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-brand-hover',
            '[&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-6',
            '[&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-6',
            '[&_li]:my-1 [&_li]:leading-7',
            '[&_blockquote]:my-4 [&_blockquote]:border-l-2 [&_blockquote]:border-border-card [&_blockquote]:pl-4 [&_blockquote]:text-text-secondary',
            '[&_code]:rounded [&_code]:bg-bg-card [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-sm [&_code]:text-text-primary',
            '[&_pre]:my-4 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-bg-card [&_pre]:p-0',
            '[&_pre_code]:block [&_pre_code]:bg-transparent [&_pre_code]:p-4 [&_pre_code]:text-sm',
            '[&_table]:my-0 [&_table]:w-full [&_table]:table-auto [&_table]:text-sm',
            '[&_img]:my-4 [&_img]:max-w-full [&_img]:rounded-lg',
            '[&_hr]:my-6 [&_hr]:border-border-card',
          )}
          options={mdOptions}
        >
          {normalizedChildren}
        </Markdown>
      </div>
      {visible && rect && (
        <SelectionMenuPortal rect={rect}>
          <Card className="rounded-md p-1 flex items-center gap-2">
            {[
              { title: 'Share', action: handleShare },
              { title: 'New Search', action: createChat },
              { title: 'Add to chat', action: addToChat },
              { title: 'Copy', action: copy },
            ].map((item, index) => (
              <Fragment key={item.title}>
                <button
                  onClick={() => item.action({ message: text })}
                  className="text-sm font-normal p-2 rounded-md transition-colors hover:bg-[#EFEFEF] dark:hover:bg-[#171717]"
                >
                  {index === 0 && isCreating && (
                    <Loader2 className="animate-spin mr-1 inline" size={16} />
                  )}
                  {t(item.title)}
                </button>
                {index < 3 && (
                  <Divider
                    orientation="vertical"
                    variant="middle"
                    flexItem
                    className="border-border-card!"
                  />
                )}
              </Fragment>
            ))}
          </Card>
        </SelectionMenuPortal>
      )}
      <ShareQuote
        open={open}
        paragraphContent={paragraphContent}
        onClose={() => setOpen(false)}
        query={query}
      />
    </>
  );
});

export default MarkdownContent;
